import { Paper, Group, Badge, Alert, Text } from "@mantine/core"
import {
  MRT_ColumnDef,
  useMantineReactTable,
  MantineReactTable,
} from "mantine-react-table"
import { Link } from "../../../../-components/link"

import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { DottedMenu } from "../../../../-components/dotted-menu"
import { NavButton } from "../../../../-components/nav-button"
import { Page } from "../../../../-components/Page"
import { AbsenceConfigStorageObject } from "../../../../@client/Absents_Db"
import { usePostMutation } from "../../../../typed-client/hooks/use-post-mutation"
import { useSuspenseQueryGet } from "../../../../typed-client/hooks/use-suspense-query-get"
import { showErrorNotification } from "../../../../utils/form-submission-notifications"
import { useBreakpoints } from "../../../../utils/use-breakpoints"
import { useNavigate, createFileRoute } from "@tanstack/react-router"
import { SuspenseWithErrorBoundary } from "@components/suspense"

export const Route = createFileRoute("/app/admin-settings/absences/")({
  component: AbsenceSettingsPage,
  beforeLoad: (context) => {
    return {
      getTitle: () =>
        context.context.t &&
        context.context.t("NavItem_AbwesenheitKonfiguration"),
    }
  },
})

function OutboxStatisics() {
  const days = 7
  const { data } = useSuspenseQueryGet("/api/Outbox/GetStatistic", {
    since: `${days.toString()}.00:00:00`,
  })

  const { t } = useTranslation("app_server")

  return (
    <Paper mt="xs" p="xs" shadow="xs">
      <Link
        navigateOptions={{ to: "/app/outbox" }}
        linkText={t("BackgroundJobsOutbox")}
      />
      <Text>{t("InLastXDays", { x: days })}:</Text>
      <Text c={data.errors.length > 0 ? "red" : "green"}>
        {t("Errors")}: {data.errors.length}
      </Text>
      <Text>
        {t("Open")}: {data.pending.length}
      </Text>
    </Paper>
  )
}

export function AbsenceSettingsPage() {
  const list = useSuspenseQueryGet("/api/LeaveRequestConfig/Index", null)
  const navigate = useNavigate()
  const { t } = useTranslation("app_server")
  const merge = usePostMutation("/api/LeaveRequestConfig/Merge", {
    onSuccess: (result) => {
      list.refetch()
      navigate({
        to: "/app/admin-settings/absences/$id",
        params: { id: result.toString() },
      })
    },
  })
  const clone = usePostMutation("/api/LeaveRequestConfig/Clone", {
    onSuccess: () => list.refetch(),
  })
  const setIsActive = usePostMutation("/api/LeaveRequestConfig/SetIsActive", {
    onSuccess: () => list.refetch(),
  })

  // const importConfig = usePostMutation("/api/LeaveRequestConfig/Import", {
  //   onSuccess: () => list.refetch(),
  // })

  const { xs } = useBreakpoints()
  const columns = useMemo<MRT_ColumnDef<AbsenceConfigStorageObject>[]>(
    () => [
      {
        accessorkey: "id",
        header: "Id",
        Cell: (v) => v.row.original.id,
      },
      {
        accessorkey: "data.name",
        header: "Name",
        Cell: (v) => v.row.original.data.name,
      },
      {
        accessorKey: "modules",
        header: t("Modules"),
        Cell: (v) => (
          <Group>
            {v.row.original.data.modules.map((v) => (
              <Badge color="violet">{v}</Badge>
            ))}
          </Group>
        ),
      },
      {
        accessorKey: "active",
        header: "Status",
        Cell: (v) =>
          v.row.original.isActive ? (
            <Badge>{t("Active")}</Badge>
          ) : (
            <Badge color="gray">{t("Inactive")}</Badge>
          ),
      },
      {
        accessorKey: "actions",
        header: "",
        Cell: ({ row: { original: v } }) => (
          <DottedMenu
            items={[
              {
                children: t("Duplicate"),
                onClick: () => {
                  clone.mutate({ id: v.id })
                },
              },
              {
                children: v.isActive ? t("Deactivate") : t("Activate"),
                onClick: (e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  setIsActive.mutate(
                    { id: v.id, isActive: !v.isActive },
                    { onError: showErrorNotification }
                  )
                },
              },
            ]}
          />
        ),
      },
    ],
    []
  )
  const table = useMantineReactTable({
    columns,
    data: list.data,
    mantineTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        navigate({
          to: "/app/admin-settings/absences/$id",
          params: {
            id: row.original.id.toString(),
          },
        })
      },
      style: { cursor: "pointer" },
    }),
    state: { density: "xs" },
    enablePagination: false,
    enableRowSelection: true,
    enableTableHead: true,
    enableSorting: false,
    enableFilters: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableFullScreenToggle: false,
    enableColumnActions: false,
    enableRowVirtualization: false,
    enableDensityToggle: false,
    enableEditing: false,
    enableColumnFilters: false,
    enableHiding: false,
    mantineTableContainerProps: {
      style: {
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      },
    },
    mantinePaperProps: {
      style: {
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      },
    },
  })

  return (
    <Page
      buttonGroupProps={
        xs
          ? undefined
          : {
              children: (
                <NavButton
                  navigateOptions={{ to: "/app/admin-settings/absences/new" }}
                >
                  {t("NewConfiguration")}
                </NavButton>
              ),
            }
      }
      dottedMenuProps={{
        items: [
          ...(table.getSelectedRowModel().rows.length > 1
            ? [
                {
                  children: t("Merge"),
                  onClick: () => {
                    merge.mutate({
                      workflowIds: table
                        .getSelectedRowModel()
                        .rows.map((r) => r.original.id),
                    })
                  },
                },
              ]
            : []),
          // {
          //   children: t("Import"),
          //   onClick: () => {
          //     modals.open({
          //       title: t("Import"),
          //       children: (
          //         <>
          //           <Form<{ value: string }>
          //             onSubmit={(v) => {
          //               importConfig.mutate(JSON.parse(v.value))
          //             }}
          //             initialValues={{ value: "{}" }}
          //           >
          //             {(f, _) => (
          //               <Stack>
          //                 <Textarea
          //                   {...f.getInputProps(_.value._PATH_)}
          //                   placeholder="JSON"
          //                 />
          //                 <SubmitButton>{t("Import")}</SubmitButton>
          //               </Stack>
          //             )}
          //           </Form>
          //         </>
          //       ),
          //     })
          //   },
          // },
          xs
            ? {
                children: t("NewConfiguration"),
                onClick: () =>
                  navigate({ to: "/app/admin-settings/absences/new" }),
              }
            : null,
        ],
      }}
    >
      <Alert color="gray" mb="xs">
        Hier können Abläufe für Abwesenheitsanträge konfiguriert werden. Für
        unterschiedliche Abwesenheitsgründe und Module können unterschiedliche
        Abläufe konfiguriert werden.
      </Alert>
      <MantineReactTable table={table} />
      <SuspenseWithErrorBoundary>
        <OutboxStatisics />
      </SuspenseWithErrorBoundary>
    </Page>
  )
}
